/* here&found — AJAX cart (assets/hf-cart.js).
   Shopify AJAX Cart API + Section Rendering API. Never a full page reload —
   except as the deliberate no-JS / network-failure fallback, because every
   add/change control in the theme is a REAL form or link first.

   Elements:
   - <hf-cart-add>  wraps a plain POST form to routes.cart_add_url (product
     card "+", video card ADD TO BAG, PDP buy buttons). With JS on, the submit
     is intercepted and sent to /cart/add.js with a `sections` list, so the
     cart drawer / any marked section re-renders in place. With JS off the
     form posts normally. If the fetch fails, the form falls back to a native
     submit — commerce never breaks.
   - <hf-cart-line> wraps one cart line (drawer or cart page, pass 3+).
     Clicks on [data-hf-line-qty] buttons (data-delta="±1") or
     [data-hf-line-remove], with the line's `key` on the element, go through
     /cart/change.js with the same section re-render.

   Re-render targets: any element with [data-hf-cart-section] (value = section
   id, or blank to use its enclosing .shopify-section id). The header count
   badge (.hf-hdr__cartcount / [data-hf-cart-count]) is updated from the cart
   payload directly, created on first add if the badge isn't rendered yet.

   After every successful mutation:  document → 'hf:cart:updated' {cart}.
   All listeners are removed on disconnectedCallback. No framework. */

(() => {
  const routes = () => (window.Theme && window.Theme.routes) || {};

  const addUrl = () => routes().cart_add_url || '/cart/add.js';
  const changeUrl = () => {
    const url = routes().cart_change_url || '/cart/change';
    return url.endsWith('.js') ? url : `${url}.js`;
  };
  const cartUrl = () => {
    const url = routes().cart_url || '/cart';
    return url.endsWith('.js') ? url : `${url}.js`;
  };

  const sectionTargets = () => Array.from(document.querySelectorAll('[data-hf-cart-section]'));

  const sectionIdFor = (el) => {
    const explicit = el.getAttribute('data-hf-cart-section');
    if (explicit) return explicit;
    const wrapper = el.closest('.shopify-section');
    return wrapper && wrapper.id ? wrapper.id.replace(/^shopify-section-/, '') : null;
  };

  const sectionIds = () => {
    const ids = sectionTargets().map(sectionIdFor).filter(Boolean);
    return Array.from(new Set(ids));
  };

  const renderSections = (sections) => {
    if (!sections) return;
    sectionTargets().forEach((el) => {
      const id = sectionIdFor(el);
      const html = id && sections[id];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.getElementById(`shopify-section-${id}`) || doc.body;
      const target = el.hasAttribute('data-hf-cart-section') && el.getAttribute('data-hf-cart-section') === ''
        ? el.closest('.shopify-section') || el
        : el;
      target.innerHTML = fresh.innerHTML;
    });
  };

  // Product-card and PDP adds use this lightweight cart controller instead
  // of Horizon's CartItemsComponent event path. After the Section Rendering
  // response replaces the drawer contents, explicitly open the fresh drawer.
  // The header cart icon remains a real /cart link by design.
  const openCartDrawer = async () => {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    await customElements.whenDefined('theme-drawer');
    if (typeof drawer.open === 'function') drawer.open();
  };

  const renderCount = (count) => {
    document.querySelectorAll('[data-hf-cart-count]').forEach((el) => {
      el.textContent = count;
      el.toggleAttribute('hidden', count === 0);
    });
    document.querySelectorAll('.hf-hdr__cart').forEach((cartLink) => {
      let badge = cartLink.querySelector('.hf-hdr__cartcount');
      if (!badge && count > 0) {
        badge = document.createElement('span');
        badge.className = 'hf-hdr__cartcount hf-tnum';
        badge.setAttribute('aria-hidden', 'true');
        cartLink.appendChild(badge);
      }
      if (badge) {
        badge.textContent = count;
        badge.toggleAttribute('hidden', count === 0);
      }
      const label = cartLink.getAttribute('aria-label');
      if (label) cartLink.setAttribute('aria-label', label.replace(/\(\d+/, `(${count}`));
    });
  };

  const announce = (cart, message = '') => {
    if (cart && typeof cart.item_count === 'number') renderCount(cart.item_count);
    const status = document.getElementById('hf-cart-status');
    if (status && message) {
      status.textContent = '';
      window.requestAnimationFrame(() => { status.textContent = message; });
    }
    document.dispatchEvent(new CustomEvent('hf:cart:updated', { detail: { cart } }));
  };

  const fetchCart = () => fetch(cartUrl(), { headers: { Accept: 'application/json' } }).then((r) => r.json());

  // Horizon owns quantity/remove actions inside its cart drawer. Mirror its
  // standard completion event into the custom here&found header badge so the
  // count never lags after a drawer interaction.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const count = cart?.totalQuantity ?? detail?.itemCount;
        if (typeof count === 'number') renderCount(count);
      })
      .catch(() => {});
  });

  class HFCartAdd extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.onSubmit = (event) => this.submit(event);
      this.form.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    }

    async submit(event) {
      event.preventDefault();
      const submitter = event.submitter || null;
      const button = submitter || this.form.querySelector('[type="submit"]');
      if (this.busy) return;
      this.busy = true;
      if (button) {
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
      }
      let hasRelated = false;
      try {
        const body = new FormData(this.form);
        // Buy-it-now: the submitter carries return_to=/checkout (native posts
        // include it automatically; FormData(form) does not)
        if (submitter && submitter.name) body.append(submitter.name, submitter.value);
        const ids = sectionIds();
        if (ids.length) {
          body.append('sections', ids.join(','));
          body.append('sections_url', window.location.pathname);
        }
        const productRoot = this.closest('hf-main-product');
        const related = productRoot
          ? Array.from(productRoot.querySelectorAll('[data-hf-related]:checked'))
          : [];
        hasRelated = related.length > 0;
        let requestBody = body;
        let headers = { Accept: 'application/json' };
        if (related.length) {
          const quantity = parseInt(String(body.get('quantity') || '1'), 10) || 1;
          const payload = {
            items: [
              { id: Number(body.get('id')), quantity },
              ...related.map((input) => ({ id: Number(input.value), quantity: 1 })),
            ],
          };
          if (ids.length) {
            payload.sections = ids.join(',');
            payload.sections_url = window.location.pathname;
          }
          requestBody = JSON.stringify(payload);
          headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
        }
        const response = await fetch(addUrl(), {
          method: 'POST',
          headers,
          body: requestBody,
        });
        if (!response.ok) throw new Error(`add failed: ${response.status}`);
        const result = await response.json();
        if (submitter && submitter.name === 'return_to' && submitter.value) {
          window.location.assign(submitter.value);
          return;
        }
        renderSections(result.sections);
        const productName = productRoot && productRoot.dataset.productTitle
          ? productRoot.dataset.productTitle
          : button && button.getAttribute('aria-label')
            ? button.getAttribute('aria-label').replace(/^Add\s+/i, '').replace(/\s+to (bag|cart)$/i, '')
            : 'Item';
        announce(await fetchCart(), `${productName} added to your bag.`);
        await openCartDrawer();
        this.classList.add('is-added');
        setTimeout(() => this.classList.remove('is-added'), 1200);
      } catch (error) {
        if (hasRelated) {
          let message = this.querySelector('[data-hf-cart-error]');
          if (!message) {
            message = document.createElement('p');
            message.setAttribute('data-hf-cart-error', '');
            message.className = 'hf-buy__error';
            message.setAttribute('role', 'alert');
            message.setAttribute('aria-live', 'assertive');
            this.appendChild(message);
          }
          message.textContent = 'Could not add the pairing. Please try again.';
          return;
        }
        // keep commerce working: fall back to the native full-page post
        this.form.removeEventListener('submit', this.onSubmit);
        this.form.submit();
        return;
      } finally {
        this.busy = false;
        if (button && this.isConnected) {
          button.removeAttribute('aria-busy');
          button.disabled = false;
        }
      }
    }
  }

  class HFCartLine extends HTMLElement {
    connectedCallback() {
      this.onClick = (event) => {
        const remove = event.target.closest('[data-hf-line-remove]');
        const qty = event.target.closest('[data-hf-line-qty]');
        const control = remove || qty;
        if (!control || !this.contains(control)) return;
        const key = control.getAttribute('data-key') || this.getAttribute('key');
        if (!key) return;
        event.preventDefault();
        const current = parseInt(this.getAttribute('quantity'), 10) || 1;
        const delta = qty ? parseInt(qty.getAttribute('data-delta'), 10) || 0 : 0;
        const quantity = remove ? 0 : Math.max(0, current + delta);
        this.change(key, quantity);
      };
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    async change(key, quantity) {
      if (this.busy) return;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      const reopenDrawer = Boolean(this.closest('#cart-drawer'));
      try {
        const payload = { id: key, quantity };
        const ids = sectionIds();
        if (ids.length) {
          payload.sections = ids.join(',');
          payload.sections_url = window.location.pathname;
        }
        const response = await fetch(changeUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error(`change failed: ${response.status}`);
        const cart = await response.json();
        renderSections(cart.sections);
        announce(cart, quantity === 0 ? 'Item removed from your bag.' : 'Bag quantity updated.');
        if (reopenDrawer && quantity > 0) await openCartDrawer();
      } catch (error) {
        window.location.href = (routes().cart_url || '/cart');
      } finally {
        this.busy = false;
        if (this.isConnected) this.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('hf-cart-add')) customElements.define('hf-cart-add', HFCartAdd);
  if (!customElements.get('hf-cart-line')) customElements.define('hf-cart-line', HFCartLine);
})();
